import type { FlatMetaFieldSchema, MetaEntityKind, SellerType } from '@/config/metadata-schemas.config'
import { getResourceFields, getResourceValues, isFieldRequired } from '@/utils/metadata-form.utils'
import { humanizeMetaKey } from '@/utils/metadata-label.utils'
import { parseMetaValue } from '@/utils/metadata-schema.utils'

/**
 * Search + filter for the metadata panel: which fields to show, and the DOM id
 * the missing-required summary jumps to.
 */

/** Panel-wide field filter, applied to the stored (snapshot) value of each field. */
export type MetaFieldFilter = 'all' | 'missing' | 'invalid' | 'set' | 'unset'

/** The panel's search query plus the active filter chip. */
export type MetaFieldView = {
  query: string
  filter: MetaFieldFilter
}

export const DEFAULT_META_FIELD_VIEW: MetaFieldView = { query: '', filter: 'all' }

/**
 * Whether the view narrows the field list at all (a non-blank query or a filter
 * other than "all").
 * @param {MetaFieldView} view - search query + filter
 * @returns {boolean} true when some fields may be hidden
 */
export const isMetaFieldViewActive = (view: MetaFieldView): boolean => (
  view.query.trim() !== '' || view.filter !== 'all'
)

/** Only the "invalid" filter pays for schema validation. */
const matchesFilter = (
  field: FlatMetaFieldSchema,
  value: string | undefined,
  filter: MetaFieldFilter,
  sellerType: SellerType,
): boolean => {
  const isSet = Boolean(value?.trim())
  switch (filter) {
  case 'missing':
    return !isSet && isFieldRequired(field, sellerType)
  case 'invalid':
    return isSet && !parseMetaValue(field, value!).valid
  case 'set':
    return isSet
  case 'unset':
    return !isSet
  case 'all':
    return true
  }
}

/**
 * Whether one field passes the panel's search + filter. The query matches the
 * human label, the raw key, or the description; the filter reads the stored
 * value, so a field doesn't vanish while it's being edited.
 * @param {FlatMetaFieldSchema} field - the field descriptor
 * @param {string | undefined} value - the field's stored wire value
 * @param {MetaFieldView} view - search query + filter
 * @param {SellerType} sellerType - exhibition (admission) or events
 * @returns {boolean} true when the field should be shown
 */
export const fieldMatchesView = (
  field: FlatMetaFieldSchema,
  value: string | undefined,
  view: MetaFieldView,
  sellerType: SellerType,
): boolean => {
  const query = view.query.trim().toLowerCase()
  const matchesQuery = !query || [humanizeMetaKey(field.key), field.key, field.description]
    .some((text) => text.toLowerCase().includes(query))

  return matchesQuery && matchesFilter(field, value, view.filter, sellerType)
}

/**
 * The fields of one resource instance that pass the panel's search + filter.
 * @param {MetaEntityKind} resourceType - the entity kind
 * @param {string} resourceId - the specific instance id
 * @param {MetadataManagementTool.Metadata[]} values - all value rows in the snapshot
 * @param {SellerType} sellerType - exhibition (admission) or events
 * @param {MetaFieldView} view - search query + filter
 * @returns {FlatMetaFieldSchema[]} matching field descriptors
 */
export const getFieldsMatchingView = (
  resourceType: MetaEntityKind,
  resourceId: string,
  values: MetadataManagementTool.Metadata[],
  sellerType: SellerType,
  view: MetaFieldView,
): FlatMetaFieldSchema[] => {
  const currentValues = getResourceValues(values, resourceType, resourceId)
  return getResourceFields(resourceType, sellerType).filter((field) => (
    fieldMatchesView(field, currentValues[field.key], view, sellerType)
  ))
}

/**
 * DOM id of a field's container, so the missing-required summary can jump to it.
 * @param {MetaEntityKind} resourceType - the entity kind
 * @param {string} resourceId - the specific instance id
 * @param {string} key - the schema key
 * @returns {string} element id
 */
export const getMetaFieldElementId = (
  resourceType: MetaEntityKind,
  resourceId: string,
  key: string,
): string => `metadata-field-${resourceType}-${resourceId}-${key}`
